"use client";

import type { Editor, Range } from "@tiptap/core";
import Document from "@tiptap/extension-document";
import HardBreak from "@tiptap/extension-hard-break";
import History from "@tiptap/extension-history";
import type { MentionNodeAttrs } from "@tiptap/extension-mention";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Text from "@tiptap/extension-text";
import { PluginKey } from "@tiptap/pm/state";
import { EditorContent, useEditor } from "@tiptap/react";
import posthog from "posthog-js";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiCheck, FiRefreshCw, FiX } from "react-icons/fi";
import AIBadge from "@/components/ui/ai-badge";
import Button from "@/components/ui/button";
import Tooltip from "@/components/ui/tooltip";
import { useAIGenerationWithStream } from "@/hooks/use-ai-generation-with-stream";
import { looksLikeRichHtml } from "@/lib/note-utils";
import { cx } from "@/lib/tailwind-utils";
import { SuggestionItem } from "@/types/suggestions";
import { buildSuggestionInsertContent } from "./insert-suggestion";
import { CustomMention } from "./mention/mention";
import { MessagePreviewToggle } from "./message-preview";
import { BubbleToolbar } from "./rich-text/bubble-toolbar";
import { buildRichTextExtensions } from "./rich-text/extensions";
import "./rich-text/rich-text.css";
import { suggestion } from "./suggestion";

/**
 * TipTap sets suggestion state to "active" as soon as `@` matches, then fetches items.
 * For static lists we use `shouldShow` with the same filter as `items` so the plugin
 * never activates when there is nothing to pick — no popup, no extra exit step.
 * Async `onSearchSuggestions` cannot be awaited in `shouldShow`, so we still dismiss
 * via exit meta when the resolved list is empty.
 */
const mentionSuggestionPluginKey = new PluginKey("mentionSuggestion");

// Space between the formatting toolbar and the line it belongs to.
const RICH_TOOLBAR_GAP = 4;
// Height assumed until the toolbar has been measured — one row of buttons, so a
// first paint lands where the measured value would have put it anyway.
const RICH_TOOLBAR_ROW_HEIGHT = 40;
function filterMentionSuggestionsByQuery(items: SuggestionItem[], query: string): SuggestionItem[] {
  const lowercaseQuery = query.toLowerCase();
  return items.filter(item => item.label.toLowerCase().includes(lowercaseQuery) || item.type.toLowerCase().includes(lowercaseQuery));
}
function scheduleMentionSuggestionExit(editor: Editor) {
  queueMicrotask(() => {
    editor.view.dispatch(editor.state.tr.setMeta(mentionSuggestionPluginKey, {
      exit: true
    }));
  });
}
export interface TiptapProps {
  content: string;
  editorClassName?: string;
  parentClassName?: string;
  placeholder?: string;
  /** Accessible name applied to the ProseMirror contenteditable */
  ariaLabel?: string;
  onUpdate?: (text: string, html: string, completeText: string) => void;
  onEnter?: () => void;
  suggestions: SuggestionItem[];
  textArea?: boolean;
  focusClasses?: boolean;
  onSuggestionOpen?: () => boolean;
  className?: string;
  resizable?: boolean;
  /** If provided, enables AI suggestion features. Function returns an async generator. */
  onGenerateAI?: (currentValue: string, feedback?: string, previousGeneration?: string) => AsyncGenerator<string>;
  /** If provided, used for async suggestion search (e.g., patient mentions). */
  onSearchSuggestions?: (query: string) => Promise<SuggestionItem[]>;
  /** Label for the AI generate button. Defaults to "Suggest..." */
  aiButtonLabel?: string;
  /**
   * When true, picking a suggestion inserts `suggestion.content` directly
   * as editor text (with hardBreak nodes for newlines) instead of a mention
   * node.
   *
   * Turn this on only for one-off surfaces — text written once, for a
   * recipient already known at compose time (a scribe note answer, a chat/SMS
   * message). There the author should see and be able to edit the real wording
   * before it goes out.
   *
   * Leave it off for anything authored once and rendered many times later
   * (templates, sequences, blasts, assistant prompts). Those need the mention
   * to survive into storage so the token is interpolated per recipient at send
   * time — and so an AI prompt receives the token rather than one sample value.
   */
  expandSuggestionsOnInsert?: boolean;
  /** Fires when the editor receives focus. */
  onFocus?: () => void;
  /** Receives the editor instance once it's been created. */
  onEditorReady?: (editor: Editor) => void;
  /**
   * If set, the editor emits a `suggestion_inserted` PostHog event each time
   * the @-mention popup is used to insert an item, with `surface` set to this
   * value. Pass `"note_inline"` from note answer editors and `"sms"` from the
   * SMS chat composer; leave undefined for surfaces outside the suggestions
   * adoption metrics so unrelated callers (tasks, settings forms) stay silent.
   */
  posthogSurface?: string;
  /**
   * When false, the editor becomes read-only — content stays visible but
   * keyboard input and selection-based commands are ignored. Defaults to true.
   * Mirrors Tiptap's own `editable` flag; toggling it at runtime calls
   * `editor.setEditable` so callers don't have to remount the editor.
   */
  editable?: boolean;
  /**
   * When true, enables inline text formatting (bold / italic / underline) with
   * a small toolbar, and persists the value as HTML instead of plain text.
   * `onUpdate`'s `text` and `completeText` args become HTML strings. Opt-in so
   * existing plain-text callers (SMS, tasks, settings) are unaffected.
   */
  richText?: boolean;
  /**
   * Offers a "Show preview" toggle that renders the text with every variable
   * replaced by its sample preview. Defaults on for template surfaces — those
   * that keep `@`-pills (`expandSuggestionsOnInsert` off) in plain text — and
   * only appears once the text names a variable.
   */
  showPreview?: boolean;
}
function transformContentToMentions(content: string, suggestions: SuggestionItem[]) {
  // Handle mentions
  const mentionRegex = /\{([^}]+)\}/g;
  const contentWithMentions = content.replace(mentionRegex, (match, variableId: string) => {
    const suggestion = suggestions.find(s => s.id === variableId);
    const label = suggestion?.label;
    if (!label) {
      return `{${variableId}}`;
    }
    return `<mention-component data-id="${variableId}" data-label="${suggestion.label}" data-content="${suggestion.content}" data-type="${suggestion.type}">@${suggestion.label}</mention-component>`;
  });

  // Convert newlines to <br />
  return contentWithMentions.replace(/\n/g, "<br />");
}

/**
 * Prepare a content string for `setContent`. In richText mode, Tiptap parses
 * the string as HTML — so a legacy plain-text answer (not our serialized rich
 * HTML) must be escaped first, or tag-like text ("I feel <great>") and entities
 * are silently dropped/decoded when the answer is opened for editing. Rich HTML
 * and non-richText callers pass through unchanged.
 */
function prepareEditorContent(content: string, suggestions: SuggestionItem[], richText: boolean) {
  const safe = richText && content && !looksLikeRichHtml(content) ? escapeHtmlText(content) : content;
  return transformContentToMentions(safe, suggestions);
}

// Helper function to render text with formatted mentions for preview
export function renderTextWithMentions(text: string, suggestions: SuggestionItem[]): (string | React.ReactElement)[] {
  if (!text) return [];
  const mentionRegex = /\{([^}]+)\}/g;
  const parts: (string | React.ReactElement)[] = [];
  let lastIndex = 0;
  let match;
  let keyCounter = 0;
  while ((match = mentionRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    // Find the suggestion
    const variableId = match[1];
    const suggestion = suggestions.find(s => s.id === variableId);
    if (suggestion) {
      // Add formatted mention
      parts.push(<span key={`mention-${keyCounter++}`} className="rounded bg-primary/10 px-[0.3rem] py-[0.1rem] text-primary">
          @{suggestion.label}
        </span>);
    } else {
      // If no suggestion found, show the raw variable
      parts.push(`{${variableId}}`);
    }
    lastIndex = mentionRegex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : [text];
}

/**
 * Serialize the editor's JSON document to plain text,
 * formatting entity mentions as {id|label} so they can be
 * parsed back into clickable elements when rendering messages.
 */
function serializeEditorJSON(json: Record<string, unknown>): string {
  if (!json || !json.content) return "";
  const blocks = json.content as Array<Record<string, unknown>>;
  return blocks.map(block => {
    if (!block.content) return "";
    const nodes = block.content as Array<Record<string, unknown>>;
    return nodes.map(node => {
      if (node.type === "mention") {
        const attrs = (node.attrs ?? {}) as Record<string, string>;
        const id = attrs.id ?? "";
        const label = attrs.label ?? "";
        // Entity-style IDs (pat_xxx, usr_xxx): {id|label}
        // Require at least one digit to distinguish from template variables
        // like first_name, payment_link which have no digits in the suffix.
        if (/^[a-z]+_(?=.*\d)[a-zA-Z0-9]+$/.test(id) && label) {
          return `{${id}|${label}}`;
        }
        return `{${id}}`;
      }
      if (node.type === "text") return node.text as string ?? "";
      if (node.type === "hardBreak") return "\n";
      return "";
    }).join("");
  }).join("\n");
}
function escapeHtmlText(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
const interpolateContent = (content: string, suggestions: SuggestionItem[],
// In richText mode `content` is HTML, so suggestion values must be escaped
// before substitution — raw `<`/`>`/`&` would otherwise corrupt the markup
// (and be dropped/reformatted when the stored HTML is re-parsed).
escapeValues = false): string => {
  let interpolatedContent = content;
  suggestions.forEach(suggestion => {
    const value = escapeValues ? escapeHtmlText(suggestion.content) : suggestion.content;
    // Replace {id} format (template variables, form links, EVENT placeholders)
    const placeholderSimple = `{${suggestion.id}}`;
    if (interpolatedContent.includes(placeholderSimple)) {
      interpolatedContent = interpolatedContent.replace(placeholderSimple, value);
    }
    // Replace {id|label} format (used by entity-style suggestions like su_xxx)
    const placeholderWithLabel = `{${suggestion.id}|`;
    if (interpolatedContent.includes(placeholderWithLabel)) {
      const regex = new RegExp(`\\{${suggestion.id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\|[^}]*\\}`, "g");
      interpolatedContent = interpolatedContent.replace(regex, value);
    }
  });
  return interpolatedContent;
};
export default function TiptapEditor({
  content,
  editorClassName,
  parentClassName,
  placeholder,
  ariaLabel,
  onUpdate,
  onEnter,
  suggestions,
  textArea = true,
  onSuggestionOpen,
  className,
  resizable = false,
  onGenerateAI,
  onSearchSuggestions,
  aiButtonLabel = "Suggest...",
  expandSuggestionsOnInsert = false,
  onFocus,
  onEditorReady,
  posthogSurface,
  editable = true,
  richText = false,
  showPreview
}: TiptapProps) {
  const previewEnabled = showPreview ?? (!expandSuggestionsOnInsert && !richText);
  const previewEnabledRef = useRef(previewEnabled);
  previewEnabledRef.current = previewEnabled;
  const [previewText, setPreviewText] = useState(content);
  useEffect(() => {
    setPreviewText(content);
  }, [content]);
  const onFocusRef = useRef(onFocus);
  const onEditorReadyRef = useRef(onEditorReady);
  useEffect(() => {
    onFocusRef.current = onFocus;
  }, [onFocus]);
  useEffect(() => {
    onEditorReadyRef.current = onEditorReady;
  }, [onEditorReady]);
  const suggestionRef = useRef<SuggestionItem[]>(suggestions);
  const onSuggestionOpenRef = useRef(onSuggestionOpen);
  const onSearchSuggestionsRef = useRef(onSearchSuggestions);
  const expandSuggestionsOnInsertRef = useRef(expandSuggestionsOnInsert);
  const posthogSurfaceRef = useRef(posthogSurface);
  useEffect(() => {
    posthogSurfaceRef.current = posthogSurface;
  }, [posthogSurface]);
  const isUserTypingRef = useRef(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // JavaScript-based resize state
  const [editorHeight, setEditorHeight] = useState<number | null>(null);
  const resizeRef = useRef<{
    startY: number;
    startHeight: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // AI state
  const [suggestionMode, setSuggestionMode] = useState(false);
  // Whether the richText formatting toolbar is shown (only while the field is
  // focused). Toolbar buttons preventDefault on mousedown so clicking them
  // doesn't blur the editor and hide the toolbar mid-click.
  const [richToolbarVisible, setRichToolbarVisible] = useState(false);
  // Where the toolbar is drawn, relative to the field's top-left corner. A note
  // answer can be many screens tall, so a toolbar pinned to the field's own top
  // edge is off-screen for every selection past the first screenful.
  const [richToolbarAnchor, setRichToolbarAnchor] = useState<{
    top: number;
    left: number;
    above: boolean;
    fieldWidth: number;
  } | null>(null);
  // The toolbar's own size decides how far right it can sit before it overhangs
  // the field and whether there is room for it above the selection. It changes
  // at runtime — the dropdowns render in flow, under the button row — and that
  // happens inside BubbleToolbar's own state, so only an observer on the element
  // sees it. Height is read back in an event handler, hence the ref alongside.
  const richToolbarSizeRef = useRef({
    width: 0,
    height: RICH_TOOLBAR_ROW_HEIGHT
  });
  const richToolbarObserverRef = useRef<ResizeObserver | null>(null);
  const richToolbarUpdateRef = useRef<(() => void) | null>(null);
  const [richToolbarWidth, setRichToolbarWidth] = useState(0);
  const measureRichToolbar = useCallback((node: HTMLDivElement | null) => {
    richToolbarObserverRef.current?.disconnect();
    richToolbarObserverRef.current = null;
    if (!node) return;
    const read = () => {
      richToolbarSizeRef.current = {
        width: node.offsetWidth,
        height: node.offsetHeight
      };
      setRichToolbarWidth(node.offsetWidth);
      // A taller toolbar can no longer fit where it was placed, so the
      // above/below choice has to be re-made with the new height.
      richToolbarUpdateRef.current?.();
    };
    const observer = new ResizeObserver(read);
    observer.observe(node);
    richToolbarObserverRef.current = observer;
    read();
  }, []);
  const [feedback, setFeedback] = useState("");
  const [feedbackHistory, setFeedbackHistory] = useState<string[]>([]);
  const [editedText, setEditedText] = useState("");
  const aiGeneration = useAIGenerationWithStream({
    onTextUpdate: text => {
      setEditedText(text);
    }
  });
  const isGenerating = aiGeneration.isGenerating;
  const generatedText = aiGeneration.generatedText;
  const error = aiGeneration.error;
  const cancel = aiGeneration.cancel;
  const reset = () => {
    setEditedText("");
    aiGeneration.reset();
  };

  // Sync edited text when generation updates
  useEffect(() => {
    if (generatedText) {
      setEditedText(generatedText);
    }
  }, [generatedText]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancel();
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [cancel]);
  const handleGenerate = useCallback(async () => {
    if (!onGenerateAI) return;
    setSuggestionMode(true);
    setFeedback("");
    setFeedbackHistory([]);
    setEditedText("");
    await aiGeneration.generate(() => onGenerateAI(content));
  }, [onGenerateAI, content, aiGeneration]);
  const handleRetry = useCallback(async () => {
    if (!onGenerateAI) return;
    await aiGeneration.generate(() => onGenerateAI(content));
  }, [onGenerateAI, content, aiGeneration]);
  const handleRegenerate = useCallback(async () => {
    if (feedback.trim()) {
      setFeedbackHistory(prev => [...prev, feedback.trim()]);
    }
    if (!onGenerateAI) return;
    await aiGeneration.regenerate(onGenerateAI, content, feedback, editedText);
    setFeedback("");
  }, [feedback, onGenerateAI, content, editedText, aiGeneration]);
  const handleReject = useCallback(() => {
    setSuggestionMode(false);
    setFeedback("");
    setFeedbackHistory([]);
    setEditedText("");
    reset();
  }, [reset]);

  // Update refs when props change
  useEffect(() => {
    onSuggestionOpenRef.current = onSuggestionOpen;
  }, [onSuggestionOpen]);
  useEffect(() => {
    onSearchSuggestionsRef.current = onSearchSuggestions;
  }, [onSearchSuggestions]);
  useEffect(() => {
    expandSuggestionsOnInsertRef.current = expandSuggestionsOnInsert;
  }, [expandSuggestionsOnInsert]);

  // JavaScript-based resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    const container = containerRef.current;
    if (!container) return;
    const editorEl = container.querySelector(".ProseMirror") as HTMLElement;
    if (!editorEl) return;
    resizeRef.current = {
      startY: clientY,
      startHeight: editorEl.offsetHeight
    };
    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";
  }, []);
  useEffect(() => {
    const handleResizeMove = (e_0: MouseEvent | TouchEvent) => {
      if (!resizeRef.current) return;
      const clientY_0 = "touches" in e_0 ? e_0.touches[0].clientY : e_0.clientY;
      const deltaY = clientY_0 - resizeRef.current.startY;
      const newHeight = Math.max(40, resizeRef.current.startHeight + deltaY);
      setEditorHeight(newHeight);
    };
    const handleResizeEnd = () => {
      if (!resizeRef.current) return;
      resizeRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.addEventListener("mousemove", handleResizeMove);
    document.addEventListener("mouseup", handleResizeEnd);
    document.addEventListener("touchmove", handleResizeMove);
    document.addEventListener("touchend", handleResizeEnd);
    return () => {
      document.removeEventListener("mousemove", handleResizeMove);
      document.removeEventListener("mouseup", handleResizeEnd);
      document.removeEventListener("touchmove", handleResizeMove);
      document.removeEventListener("touchend", handleResizeEnd);
    };
  }, []);
  const getSuggestionItems = ({
    query
  }: {
    query: string;
  }): SuggestionItem[] | Promise<SuggestionItem[]> => {
    if (onSearchSuggestionsRef.current) {
      return onSearchSuggestionsRef.current(query);
    }
    return filterMentionSuggestionsByQuery(suggestionRef.current, query);
  };
  const editor_2 = useEditor({
    immediatelyRender: false,
    editable,
    extensions: [Document, Paragraph, Text, HardBreak, History, ...(richText ? buildRichTextExtensions(editable) : []), Placeholder.configure({
      placeholder
    }), CustomMention.configure({
      renderText: ({
        node: node_0
      }) => {
        // Entity mentions (pat_xxx, usr_xxx, etc.): store as {id|label}
        // so message bubbles can render them as styled pills.
        // Template variables (first_name, payment_link) keep {id} format.
        // Require at least one digit in the suffix to distinguish from templates.
        const id = node_0.attrs.id ?? "";
        if (/^[a-z]+_(?=.*\d)[a-zA-Z0-9]+$/.test(id) && node_0.attrs.label) {
          return `{${id}|${node_0.attrs.label}}`;
        }
        return `{${id}}`;
      },
      suggestion: {
        pluginKey: mentionSuggestionPluginKey,
        items: getSuggestionItems,
        shouldShow: ({
          query: query_0
        }: {
          query: string;
        }) => {
          if (onSearchSuggestionsRef.current) {
            return true;
          }
          if (suggestionRef.current.length === 0) {
            return false;
          }
          return filterMentionSuggestionsByQuery(suggestionRef.current, query_0).length > 0;
        },
        command: (args: {
          editor: Editor;
          range: Range;
          props: MentionNodeAttrs;
        }) => {
          const {
            editor,
            range
          } = args;
          const item = args.props as unknown as SuggestionItem;
          const surface = posthogSurfaceRef.current;
          if (surface) {
            posthog.capture("suggestion_inserted", {
              suggestion_id: item.id,
              suggestion_type: item.type,
              surface
            });
          }
          // Mirror the default mention command's handling of a trailing space
          // so `@foo ` doesn't leave an extra space behind after insertion.
          const nodeAfter = editor.view.state.selection.$to.nodeAfter;
          const overrideSpace = nodeAfter?.text?.startsWith(" ");
          const insertRange = overrideSpace ? {
            from: range.from,
            to: range.to + 1
          } : range;

          // Expanded: the suggestion's content goes in as editor text, with
          // newlines as hardBreak nodes so it reads WYSIWYG instead of as an
          // @-pill. Otherwise: a mention node + trailing space, the same
          // content the "Insert variable" modal path produces.
          const content_0 = buildSuggestionInsertContent(item, {
            expand: expandSuggestionsOnInsertRef.current
          });
          if (Array.isArray(content_0) && content_0.length === 0) {
            // Expanded but empty: just clear the `@query` range. We can't
            // push an empty text node — ProseMirror's Text.fromJSON rejects
            // it, and insertContent([]) is a no-op anyway.
            editor.chain().focus().deleteRange(insertRange).run();
            return;
          }
          editor.chain().focus().deleteRange(insertRange).insertContent(content_0).run();
        },
        ...suggestion,
        render: () => {
          const baseRender = suggestion.render({
            insertsText: () => expandSuggestionsOnInsertRef.current
          });
          return {
            ...baseRender,
            onStart: (props: {
              editor: Editor;
              items: SuggestionItem[];
            }) => {
              let shouldOpen = true;
              if (onSuggestionOpenRef.current) {
                shouldOpen = onSuggestionOpenRef.current();
              }
              if (!shouldOpen) {
                return;
              }
              if (props.items.length === 0 && onSearchSuggestionsRef.current) {
                scheduleMentionSuggestionExit(props.editor);
                return;
              }
              baseRender.onStart?.(props);
            },
            onUpdate: (props_0: {
              editor: Editor;
              items: SuggestionItem[];
            }) => {
              if (props_0.items.length === 0 && onSearchSuggestionsRef.current) {
                scheduleMentionSuggestionExit(props_0.editor);
                return;
              }
              baseRender.onUpdate?.(props_0);
            }
          };
        }
      }
    })],
    content: prepareEditorContent(content, suggestions, richText),
    editorProps: {
      attributes: {
        ...(ariaLabel ? {
          "aria-label": ariaLabel
        } : {}),
        class: cx(
        // Base styles
        "prose prose-sm", "max-w-none w-full", "rounded-md", "border hover:border-border", "px-3 py-1 m-0", textArea ? "min-h-[2.5rem] overflow-y-auto resize-none" : "min-h-[2.5rem] overflow-y-auto",
        // Remove focus ring styles
        "focus:ring-0 focus:outline-none focus:border-border",
        // Custom classes
        editorClassName)
      },
      handleKeyDown: (view, event) => {
        // Handle Enter key
        if (event.key === "Enter" && !event.shiftKey) {
          // Check if suggestion menu is active by looking for the suggestion element
          const suggestionMenu = document.querySelector(".tippy-box");
          if (suggestionMenu) {
            return false; // Let the suggestion menu handle the enter key
          }

          // If no suggestion menu is active and we have an onEnter handler, use it
          if (onEnter) {
            event.preventDefault();
            event.stopPropagation();
            onEnter();
            return true;
          }
        }
        return false;
      }
    },
    parseOptions: {
      preserveWhitespace: "full"
    },
    onCreate: ({
      editor: editor_0
    }) => {
      onEditorReadyRef.current?.(editor_0 as Editor);
    },
    onFocus: () => {
      onFocusRef.current?.();
    },
    onUpdate: ({
      editor: editor_1
    }) => {
      if (previewEnabledRef.current) {
        setPreviewText(serializeEditorJSON(editor_1.getJSON()));
      }
      if (onUpdate) {
        // Mark that user is typing to prevent content prop updates from triggering setContent
        isUserTypingRef.current = true;

        // Clear any existing timeout
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
        const json = editor_1.getJSON();
        // richText persists the full editor HTML (headings/lists/quote/colors).
        // Only the canonical empty paragraph counts as blank -> "": editor.isEmpty
        // also reports true for textless-but-structural blocks (an empty list or
        // heading), which we must keep so they don't vanish on save.
        const richHtml = editor_1.getHTML();
        const text_0 = richText ? richHtml === "<p></p>" ? "" : richHtml : serializeEditorJSON(json);
        const completeText = interpolateContent(text_0, suggestions, richText);
        onUpdate(text_0, editor_1.getHTML(), completeText);

        // Reset after a delay - this prevents the content useEffect from calling
        // setContent while the user is actively typing
        typingTimeoutRef.current = setTimeout(() => {
          isUserTypingRef.current = false;
        }, 300);
      }
    }
  });

  // Tiptap reads `editable` only at init; mirror prop changes through
  // setEditable so a parent toggling the lock state (e.g. pre- vs
  // post-generation) doesn't have to remount the editor.
  useEffect(() => {
    if (!editor_2) return;
    if (editor_2.isEditable !== editable) editor_2.setEditable(editable);
  }, [editor_2, editable]);

  // Show the richText formatting toolbar only while text is selected. Toolbar
  // buttons preventDefault on mousedown, so clicking them keeps the selection
  // (and focus) and the toolbar stays open.
  useEffect(() => {
    if (!editor_2 || !richText) return;
    let frame = 0;
    const update = () => {
      const {
        from,
        to
      } = editor_2.state.selection;
      const visible = editor_2.isEditable && editor_2.isFocused && from !== to;
      setRichToolbarVisible(visible);
      const container_0 = containerRef.current;
      if (!visible || !container_0) return;
      // Both rects are viewport-based, so the difference is a container-relative
      // offset — what the toolbar is positioned from, since it scrolls with the
      // text it belongs to.
      const fieldRect = container_0.getBoundingClientRect();
      const start = editor_2.view.coordsAtPos(from);
      const end = editor_2.view.coordsAtPos(to);
      const clearance = richToolbarSizeRef.current.height + RICH_TOOLBAR_GAP;
      // Whether to sit above the selection is a separate question, and one the
      // container-relative offset cannot answer on its own: a note answer many
      // screens tall leaves room to spare inside the field while the selection
      // itself is at the top of the window. Both have to hold — room in the
      // viewport so the toolbar is on screen, room in the field so it doesn't
      // ride up over the page chrome above the editor.
      const above = start.top >= clearance && start.top - fieldRect.top >= clearance;
      // Anchor horizontally to the same end of the selection it hangs off
      // vertically, or a selection spanning several lines puts the toolbar under
      // its last line but beside the first line's start.
      const edge = above ? start : end;
      const next = {
        top: (above ? start.top : end.bottom) - fieldRect.top,
        left: edge.left - fieldRect.left,
        above,
        fieldWidth: fieldRect.width
      };
      setRichToolbarAnchor(prev_0 => prev_0 && prev_0.top === next.top && prev_0.left === next.left && prev_0.above === next.above && prev_0.fieldWidth === next.fieldWidth ? prev_0 : next);
    };
    richToolbarUpdateRef.current = update;
    // Scrolling moves the selection through the viewport without touching the
    // document, so the above/below choice has to be re-made or it goes stale on
    // a standing selection. Everything else in the anchor is field-relative and
    // untouched by a scroll, which is what keeps the bail above from re-rendering
    // on every frame. Capture, to catch scrolls in nested containers.
    const onViewportChange = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    const onBlur = () => setRichToolbarVisible(false);
    editor_2.on("selectionUpdate", update);
    editor_2.on("transaction", update);
    editor_2.on("focus", update);
    editor_2.on("blur", onBlur);
    window.addEventListener("scroll", onViewportChange, {
      passive: true,
      capture: true
    });
    window.addEventListener("resize", onViewportChange);
    return () => {
      cancelAnimationFrame(frame);
      richToolbarUpdateRef.current = null;
      editor_2.off("selectionUpdate", update);
      editor_2.off("transaction", update);
      editor_2.off("focus", update);
      editor_2.off("blur", onBlur);
      window.removeEventListener("scroll", onViewportChange, {
        capture: true
      });
      window.removeEventListener("resize", onViewportChange);
    };
  }, [editor_2, richText]);
  const handleAccept = useCallback(() => {
    if (onUpdate && editor_2) {
      // Transform the AI-generated text to ensure variables are formatted as mentions
      const transformedContent = transformContentToMentions(editedText, suggestions);

      // Immediately update the editor content with transformed mentions
      // This ensures mentions are displayed correctly when we exit suggestion mode
      editor_2.commands.setContent(transformedContent, {
        emitUpdate: false,
        parseOptions: {
          preserveWhitespace: "full"
        }
      });

      // Update the form field with the plain text (variables in {variable} format)
      // This ensures the form stores the correct format for persistence
      onUpdate(editedText, transformedContent, editedText);
    }
    setSuggestionMode(false);
    setFeedback("");
    setFeedbackHistory([]);
    setEditedText("");
    reset();
  }, [onUpdate, editedText, suggestions, reset, editor_2]);
  const handleKeyDown = useCallback((e_1: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e_1.key === "Tab" && editedText && !error) {
      e_1.preventDefault();
      handleAccept();
    } else if (e_1.key === "Enter") {
      e_1.preventDefault();
      if (feedback.trim()) {
        handleRegenerate();
      } else {
        handleRetry();
      }
    }
  }, [feedback, handleRegenerate, handleRetry, editedText, error, handleAccept]);

  // Function to handle external content updates (only called when editor is NOT focused).
  // Deferred via setTimeout so Tiptap's internal flushSync doesn't collide with React rendering.
  const updateContent = useCallback((newContent: string) => {
    if (editor_2) {
      setTimeout(() => {
        // Editor may be destroyed, or user may have started typing, between
        // scheduling and firing — either case makes the overwrite unsafe.
        if (editor_2.isDestroyed || editor_2.isFocused) return;
        const transformedContent_0 = prepareEditorContent(newContent, suggestions, richText);
        editor_2.commands.setContent(transformedContent_0, {
          emitUpdate: false,
          parseOptions: {
            preserveWhitespace: "full"
          }
        });
      }, 0);
    }
  }, [editor_2, suggestions, richText]);

  // Effect only for external content changes
  useEffect(() => {
    if (!editor_2) return;

    // Skip if we're in suggestion mode (we'll handle it when accepting)
    if (suggestionMode) return;
    const editorText = editor_2.getText({
      blockSeparator: "\n"
    });

    // Handle clearing content (empty string) - always allow this, even when typing/focused
    // This ensures the editor clears after sending a message
    if (content === "" && editorText !== "") {
      editor_2.commands.clearContent(false);
      return;
    }

    // Skip if user is actively typing - prevents scroll jumps when content prop updates
    if (isUserTypingRef.current) return;

    // Skip if editor is focused - user is actively editing, don't interfere
    if (editor_2.isFocused && content !== "") return;
    if (!content) return;

    // In richText mode the stored content is HTML, so compare against the
    // editor's HTML instead of its plain text to avoid needless re-parsing.
    const currentValue = richText ? editor_2.getHTML() : editorText;

    // Normalize both strings for comparison
    const normalizedContent = content.replace(/\n+$/, "");
    const normalizedEditorText = currentValue.replace(/\n+$/, "");

    // Only update if content actually differs
    if (normalizedContent !== normalizedEditorText) {
      updateContent(content);
    }
  }, [content, editor_2, updateContent, suggestionMode, richText]);

  // Track previous suggestions to detect actual changes
  const prevSuggestionsRef = useRef<SuggestionItem[]>([]);
  useEffect(() => {
    // Only update when suggestions actually change (not on every content change)
    const suggestionsChanged = suggestions.length !== prevSuggestionsRef.current.length || suggestions.some((s, i) => s.id !== prevSuggestionsRef.current[i]?.id || s.content !== prevSuggestionsRef.current[i]?.content);
    if (suggestionsChanged && suggestions.length > 0) {
      // Only update content if editor is not focused (user not actively typing)
      if (!editor_2?.isFocused) {
        updateContent(content);
      }
      suggestionRef.current = suggestions;
      prevSuggestionsRef.current = suggestions;
    }
  }, [suggestions, updateContent, content, editor_2]);

  // If onGenerateAI is provided, wrap with AI features
  if (onGenerateAI) {
    return <div className={cx("overflow-hidden rounded-lg border border-border bg-background transition-all", suggestionMode && "shadow-sm", className)}>
        {/* Content area */}
        {suggestionMode ? <div className="relative">
            {error ? <div className="min-h-[100px] p-3 text-body text-status-error-text">
                Error: {error}
              </div> : isGenerating ? <div className="min-h-[100px] whitespace-pre-wrap p-3 text-body text-foreground">
                {generatedText ? <>
                    {renderTextWithMentions(generatedText, suggestions).map((part, index) => <React.Fragment key={index}>{part}</React.Fragment>)}
                    <span className="ml-0.5 inline-block h-4 w-0.5 animate-pulse bg-primary" />
                  </> : <span className="text-foreground-muted">Generating...</span>}
              </div> : <>
                <div className="min-h-[100px] whitespace-pre-wrap p-3 text-body text-foreground">
                  {editedText ? renderTextWithMentions(editedText, suggestions).map((part_0, index_0) => <React.Fragment key={index_0}>{part_0}</React.Fragment>) : <span className="text-foreground-muted">
                      AI generated content will appear here...
                    </span>}
                </div>
                {/* Transparent textarea overlay for editing */}
                <textarea value={editedText} onChange={e_2 => setEditedText(e_2.target.value)} className="absolute inset-0 min-h-[100px] w-full resize-none rounded-none border-0 bg-transparent p-3 text-body text-transparent caret-gray-900 shadow-none outline-none ring-0 focus:shadow-none focus:outline-none focus:ring-0" placeholder="" style={{
            color: "transparent",
            caretColor: "#111827"
          }} />
              </>}
          </div> : <EditorContent className={cx(parentClassName, "[&_.ProseMirror]:rounded-none [&_.ProseMirror]:border-0 [&_.ProseMirror]:shadow-none", "[&_.ProseMirror]:focus:border-border [&_.ProseMirror]:focus:outline-none [&_.ProseMirror]:focus:ring-0")} editor={editor_2} />}

        {previewEnabled && !suggestionMode && <MessagePreviewToggle text={previewText} suggestions={suggestions} className="border-t border-border px-3 py-2" />}

        {/* AI Toolbar */}
        {suggestionMode ? <div className="border-t border-border bg-background-secondary">
            <div className="flex items-center justify-between px-2 py-1.5">
              <div className="flex items-center gap-1.5">
                <AIBadge size={14} />
                <span className="text-caption-strong text-primary">
                  AI Suggestion
                </span>
                {isGenerating && <span className="text-caption text-foreground-muted">
                    generating...
                  </span>}
                {feedbackHistory.length > 0 && !isGenerating && <span className="text-caption text-foreground-muted">
                    · {feedbackHistory.length} revision
                    {feedbackHistory.length > 1 ? <span>{"s"}</span> : <span>{""}</span>}
                  </span>}
              </div>

              {/* Icon buttons */}
              {!isGenerating && (editedText || error) && <div className="flex items-center gap-0.5">
                  {editedText && !error && <Tooltip content="Accept (Tab)">
                      <Button type="button" variant="ghost" size="icon-sm" onClick={handleAccept} className="text-primary hover:bg-accent hover:text-primary">
                        <FiCheck className="h-4 w-4" />
                      </Button>
                    </Tooltip>}
                  <Tooltip content="Regenerate">
                    <Button type="button" variant="ghost" size="icon-sm" onClick={handleRetry} className="text-muted-foreground hover:text-foreground-secondary">
                      <FiRefreshCw className="h-4 w-4" />
                    </Button>
                  </Tooltip>
                  <Tooltip content="Cancel">
                    <Button type="button" variant="ghost" size="icon-sm" onClick={handleReject} className="text-muted-foreground hover:text-status-error-text">
                      <FiX className="h-4 w-4" />
                    </Button>
                  </Tooltip>
                </div>}

              {isGenerating && <Tooltip content="Cancel">
                  <Button type="button" variant="ghost" size="icon-sm" onClick={() => {
              cancel();
              setSuggestionMode(false);
              setFeedbackHistory([]);
              setEditedText("");
            }} className="text-muted-foreground hover:text-status-error-text">
                    <FiX className="h-4 w-4" />
                  </Button>
                </Tooltip>}
            </div>

            {/* Feedback input */}
            {!isGenerating && <input type="text" placeholder="Add feedback and press Enter to regenerate..." value={feedback} onChange={e_3 => setFeedback(e_3.target.value)} onKeyDown={handleKeyDown} style={{
          outline: "none",
          boxShadow: "none"
        }} className="w-full appearance-none border-0 border-t border-border bg-transparent px-3 py-2 text-body text-foreground-secondary placeholder:text-foreground-muted" />}
          </div> : <button type="button" onClick={handleGenerate} className="flex w-full items-center gap-1.5 border-t border-border bg-background-secondary px-2 py-1.5 transition-colors hover:bg-hover">
            <AIBadge size={14} />
            <span className="text-caption-strong text-primary">
              {aiButtonLabel}
            </span>
          </button>}
      </div>;
  }
  return <>
      <div ref={containerRef} className={cx("relative", parentClassName)}>
        {richText && editable && editor_2 && richToolbarVisible && richToolbarAnchor &&
      // Floats beside the selection (absolute, so it doesn't push page
      // content) but stays in the editor's own DOM — not a body portal — so
      // it's always clickable regardless of dialog/pointer-events context.
      <div ref={measureRichToolbar} className={cx("absolute z-20 w-fit max-w-full overflow-x-auto", richToolbarAnchor.above && "-translate-y-full")} style={{
        top: richToolbarAnchor.above ? richToolbarAnchor.top - RICH_TOOLBAR_GAP : richToolbarAnchor.top + RICH_TOOLBAR_GAP,
        left: Math.max(0, Math.min(richToolbarAnchor.left, richToolbarAnchor.fieldWidth - richToolbarWidth))
      }}>
              <BubbleToolbar editor={editor_2} />
            </div>}
        <EditorContent editor={editor_2} className={cx(richText && "tiptap-rich", resizable && editorHeight && "[&_.ProseMirror]:!h-[var(--editor-height)]")} style={resizable && editorHeight ? {
        "--editor-height": `${editorHeight}px`
      } as React.CSSProperties : undefined} />
        {resizable && <div className={cx("absolute bottom-1 right-1 z-10 flex h-6 w-6 cursor-ns-resize items-center justify-center", "rounded border border-border/30 bg-background", "text-foreground-muted transition-opacity", "hover:opacity-100 hover:border-border", "active:opacity-100 active:bg-hover")} style={{
        touchAction: "none"
      }} onMouseDown={handleResizeStart} onTouchStart={handleResizeStart} aria-label="Resize editor">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" className="opacity-70">
              <path d="M11 11L3 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M11 8L3 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M11 5L3 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>}
      </div>
      {previewEnabled && <MessagePreviewToggle text={previewText} suggestions={suggestions} className="mt-1" />}
    </>;
}